import { ChevronRight, DateRange } from '@mui/icons-material';
import { Box, Dialog, DialogContent, Typography } from '@mui/material';
import { TRACKABLE_EVENTS, trackEvent } from 'helpers/trackerHelpers';
import React from 'react';
import { useTranslation } from 'react-i18next';
import BudgetKeyDates from 'routes/Home/components/BudgetKeyDates/BudgetKeyDates';
import { BUDGET_KEY_DATES_EVENT } from 'routes/Home/components/BudgetKeyDates/constants';
import {
    getActiveStep,
    getFormattedScheduledDate,
    getKeyDateLabel,
} from 'routes/Home/components/BudgetKeyDates/helpers';
import { useGetBudgetKeyDates } from 'routes/Home/hooks/useGetBudgetKeyDates';

type Props = {
    exercice: number | string | null;
};

/**
 * Displays the next budget key date (or the last one if all key dates have passed) for the current exercice.
 * Once all dates have passed for current exercice and there is a new one open, we display the first date of the
 * next one.
 * On click, it opens a modal displaying all keys dates, with the ability to edit them for admins.
 */
const KeyDatesHeaderWidget = ({ exercice }: Props) => {
    const [isModalOpened, setIsModalOpened] = React.useState(false);
    const { t } = useTranslation();
    const { data, isLoading } = useGetBudgetKeyDates();
    if (!data || !exercice || isLoading) return null;
    const keyDates = data[exercice.toString()];
    if (!keyDates) return null;
    const activeEventIndex = getActiveStep(keyDates, BUDGET_KEY_DATES_EVENT);

    const nextStepEventIndex =
        activeEventIndex < keyDates.length - 1
            ? activeEventIndex + 1
            : activeEventIndex;
    const activeEvent = keyDates[nextStepEventIndex];
    const nextDateLabel =
        nextStepEventIndex === keyDates.length - 1
            ? t('budget.keyDates.last')
            : t('budget.keyDates.next');
    const onWidgetClick = () => {
        trackEvent(TRACKABLE_EVENTS.budget.ux.keyDatesWidgetClick);
        setIsModalOpened(true);
    };
    return (
        <>
            <Box
                sx={{
                    backgroundColor: 'primary.background',
                    padding: 2,
                    alignItems: 'center',
                    gap: 2,
                    display: 'flex',
                    height: '100%',
                    '&:hover': {
                        cursor: 'pointer',
                    },
                }}
                onClick={onWidgetClick}
            >
                <DateRange />
                <Box sx={{ whiteSpace: 'nowrap' }}>
                    <Typography>{nextDateLabel}</Typography>
                    <Typography fontWeight={600}>
                        {getKeyDateLabel(activeEvent, t)}
                    </Typography>
                    <Typography>
                        {getFormattedScheduledDate(activeEvent.scheduledAt, t)}
                    </Typography>
                </Box>
                <ChevronRight />
            </Box>
            <Dialog
                open={isModalOpened}
                onClose={() => setIsModalOpened(false)}
                maxWidth="md"
            >
                <DialogContent>
                    <BudgetKeyDates onConfirm={() => setIsModalOpened(false)} />
                </DialogContent>
            </Dialog>
        </>
    );
};

export default KeyDatesHeaderWidget;
